import { AxiosResponse, AxiosError } from 'axios';
import { CommonAppService } from '../commonApp.service';

interface ConvertioFileAttributes {
  base64: string | null;
}

interface ConversionStartResponse {
  id: string;
}

const MAX_SIZE_KB = 2000 * 1024; // 2000KB in bytes

interface FileTypeConfig {
  mimeType: string;
  extension: string;
}

const SUPPORTED_FILES: FileTypeConfig[] = [
  { mimeType: 'application/pdf', extension: '.pdf' },
  { mimeType: 'application/msword', extension: '.doc' },
  { mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: '.docx' },
  { mimeType: 'text/plain', extension: '.txt' },
  { mimeType: 'text/rtf', extension: '.rtf' },
];

export const SUPPORTED_FILE_TYPES = SUPPORTED_FILES.map((type) => ({
  mimeType: type.mimeType,
  extension: type.extension,
}));

class FileConversionService extends CommonAppService {
  constructor() {
    super();
  }

  public convertToPDF = async (file: File): Promise<File | undefined> => {
    try {
      this.validateFile(file);

      if (file.type === 'application/pdf') {
        return file;
      }

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          resolve(base64String.split(',')[1]);
        };
        reader.readAsDataURL(file);
      });

      const response = await this.post<ConversionStartResponse, { fileBase64: string }>('/file-conversion/pdf', {
        fileBase64: base64,
      }).then(this.success);

      const { id } = response as ConversionStartResponse;

      const maxAttempts = 10;
      const delayMs = 1000;

      for (let attempts = 0; attempts < maxAttempts; attempts++) {
        const result = await this.get<ConvertioFileAttributes, AxiosResponse<ConvertioFileAttributes>>(
          `/file-conversion/download/${id}`,
        ).then(this.success);

        if (result?.base64) {
          return this.createPDFFile(file.name, result.base64);
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      throw new Error('Conversion timed out');
    } catch (error) {
      this.error(error as AxiosError<Error>);
      return undefined;
    }
  };

  private createPDFFile(originalName: string, base64: string): File {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return new File([blob], originalName.replace(/\.[^/.]+$/, '.pdf'), { type: 'application/pdf' });
  }

  private validateFile(file: File): void {
    if (!SUPPORTED_FILES.some((type) => type.mimeType === file.type)) {
      throw new Error(`Unsupported file type: ${file.type}`);
    }

    if (file.size > MAX_SIZE_KB) {
      throw new Error(`File size exceeds maximum limit of ${MAX_SIZE_KB / 1024}KB`);
    }
  }
}

export const fileConversionService = new FileConversionService();
